import { useRef, useState } from 'react';

import {
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Stack,
  Text,
} from '@chakra-ui/react';

import { CloseCircleOutline, CezanneCompanySyncResult } from '@aslanpay/portal-common-lib';

import Step from 'app/components/UI/Step';
import useStep from 'app/hooks/useStep';

import CezanneCredentialsForm from '../CezanneCredentialsForm';
import { CezanneCredentialsFormFields } from '../CezanneCredentialsForm/types';
import CezanneSyncReview from '../CezanneSyncReview';

import { ConnectCezanneWizardModalProps } from './types';

const STEPS = [
  { id: 'cezanne-credentials-form', name: 'Connect' },
  { id: 'cezanne-sync-review', name: 'Review & confirm' },
];

interface SyncState {
  result: CezanneCompanySyncResult;
  credentials: CezanneCredentialsFormFields;
}

/** ASD-4330: an additive alternative to CreateCompanyModal for onboarding a brand-new Cezanne customer -
 * connects with raw tenant credentials, validates before anything is created, then confirms creation. Doesn't
 * touch the existing manual "Create Company" or "attach connection to an existing company" flows.
 *
 * The sync is a single synchronous call (see useCezanneCompanySync) - nothing is persisted server-side, so the
 * result and the credentials that produced it are held here in memory for the duration of the wizard only, and
 * passed to the review step so it can build the confirm request without asking the user to re-enter anything. */
function ConnectCezanneWizardModal({ isOpen, onClose, onBack }: ConnectCezanneWizardModalProps) {
  const [syncState, setSyncState] = useState<SyncState | undefined>(undefined);
  // Whether company creation has actually committed for the current review step - once true, the footer
  // button below reads "Close" instead of "Cancel", since there is nothing left to cancel by that point (see
  // CezanneSyncReview's onConfirmed).
  const [hasConfirmed, setHasConfirmed] = useState(false);
  const footerRef = useRef<HTMLElement>(null);

  const { currentStep, nextStep, resetSteps } = useStep(STEPS.length);

  const handleClose = () => {
    setSyncState(undefined);
    setHasConfirmed(false);
    resetSteps();
    onClose();
  };

  // On the first step there's nowhere internal to go back to - hand off to the parent's onBack (returning to
  // the create-choice modal), resetting the same way handleClose does.
  const handleBackToChoice = () => {
    setSyncState(undefined);
    setHasConfirmed(false);
    resetSteps();
    onBack?.();
  };

  const handleSynced = (
    result: CezanneCompanySyncResult,
    credentials: CezanneCredentialsFormFields
  ) => {
    setSyncState({ result, credentials });
    nextStep();
  };

  const handleStartOver = () => {
    setSyncState(undefined);
    setHasConfirmed(false);
    resetSteps();
  };

  const renderCurrentStep = () => {
    if (STEPS[currentStep].id === 'cezanne-sync-review' && syncState) {
      return (
        <CezanneSyncReview
          ref={footerRef}
          result={syncState.result}
          credentials={syncState.credentials}
          onStartOver={handleStartOver}
          onConfirmed={() => setHasConfirmed(true)}
        />
      );
    }

    return (
      <CezanneCredentialsForm
        id={STEPS[0].id}
        ref={footerRef}
        onSynced={handleSynced}
        onBack={handleBackToChoice}
      />
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      onClose={handleClose}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Text as="h6">Connect via Cezanne</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close connect via Cezanne"
              onClick={handleClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody>
          <Stack spacing="40px">
            <HStack spacing="0" flexGrow={1} alignItems="flex-start">
              {STEPS.map((step, id) => (
                <Step
                  key={step.name}
                  number={id + 1}
                  name={step.name}
                  isActive={currentStep === id}
                  isCompleted={currentStep > id}
                  isFirstStep={id === 0}
                  isLastStep={STEPS.length === id + 1}
                />
              ))}
            </HStack>
            {renderCurrentStep()}
          </Stack>
        </ModalBody>
        <ModalFooter ref={footerRef}>
          <Spacer />
          <Button variant="outline" size="sm" onClick={handleClose}>
            {hasConfirmed ? 'Close' : 'Cancel'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default ConnectCezanneWizardModal;
